import React, { useState, useEffect } from 'react';
// import { useAuth } from '../context/AuthContext';
import { verisureService } from '../services/api';
import { VerisureClaim, VerisureItem, VerisureRepair } from '../types';
import { toast } from 'react-hot-toast';

export const ClaimsPage: React.FC = () => {
  // const { user } = useAuth();
  const [claims, setClaims] = useState<VerisureClaim[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedClaim, setSelectedClaim] = useState<VerisureClaim | null>(null);

  // Expanded Data state for Modal
  const [claimItem, setClaimItem] = useState<VerisureItem | null>(null);
  const [claimRepair, setClaimRepair] = useState<VerisureRepair | null>(null);

  useEffect(() => {
    fetchClaims();
  }, []);

  const fetchClaims = async () => {
    setLoading(true);
    try {
      const data = (await verisureService.getClaims()).data;
      setClaims(data.sort((a: any, b: any) => b.submittedAt - a.submittedAt));
    } catch (err: any) {
      toast.error('Failed to load claims: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (claimId: string, newStatus: string) => {
    try {
      await verisureService.updateClaimStatus(claimId, newStatus);
      toast.success(`Claim marked as ${newStatus}`);
      fetchClaims();
      setSelectedClaim(null);
    } catch (err: any) {
      toast.error('Failed to update status: ' + err.message);
    }
  };

  const openClaimModal = async (claim: VerisureClaim) => {
    setSelectedClaim(claim);
    try {
      // Fetch associated repair and item data
      const itemData = (await verisureService.getItemById(claim.itemId)).data;
      setClaimItem(itemData);
      // We would ideally fetch repair by ID, but since we didn't add getRepairById, we fetch all for item and filter
      const repairs = (await verisureService.getRepairsByItem(claim.itemId)).data;
      setClaimRepair(repairs.find((r: VerisureRepair) => r.id === claim.repairId) || null);
    } catch (err) {
      console.error(err);
    }
  };

  const closeClaimModal = () => {
    setSelectedClaim(null);
    setClaimItem(null);
    setClaimRepair(null);
  };

  return (
    <div className="p-3 sm:p-6 max-w-7xl mx-auto font-sans min-w-0 max-w-full overflow-hidden">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2 flex items-center gap-2.5 sm:gap-3">
          <span>Insurance Claims &amp; Evidence Portal</span>
          <button onClick={fetchClaims} className="p-1.5 sm:p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-full transition-colors border border-slate-700" title="Refresh Data">
            <svg className="w-3.5 sm:w-4 h-3.5 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
          </button>
        </h1>
        <p className="text-slate-400 text-xs sm:text-sm">Review, verify, and compile tamper-proof maintenance evidence packets for insurance claim submission and adjuster audit.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
        <div className="bg-amber-500/10 border border-amber-500/20 p-3.5 sm:p-5 rounded-xl">
          <div className="text-[10px] sm:text-xs text-amber-500 font-bold uppercase tracking-wider mb-1">Pending Review</div>
          <div className="text-2xl sm:text-3xl font-black text-amber-400">{claims.filter(c => c.status === 'pending').length}</div>
        </div>
        <div className="bg-emerald-500/10 border border-emerald-500/20 p-3.5 sm:p-5 rounded-xl">
          <div className="text-[10px] sm:text-xs text-emerald-500 font-bold uppercase tracking-wider mb-1">Approved Claims</div>
          <div className="text-2xl sm:text-3xl font-black text-emerald-400">{claims.filter(c => c.status === 'approved').length}</div>
        </div>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
        <div className="w-full max-w-full min-w-0 overflow-x-auto mobile-table-scroll-container">
          <table className="w-full min-w-[650px] text-left border-collapse">
            <thead>
              <tr className="bg-slate-950 border-b border-slate-800 text-[11px] sm:text-xs font-bold text-slate-400 uppercase tracking-wider">
                <th className="p-3 sm:p-4">Reference</th>
                <th className="p-3 sm:p-4">Owner Name</th>
                <th className="p-3 sm:p-4">Claim Amount</th>
                <th className="p-3 sm:p-4">Date Filed</th>
                <th className="p-3 sm:p-4">Status</th>
                <th className="p-3 sm:p-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {loading ? (
                <tr><td colSpan={6} className="p-8 text-center text-slate-500">Loading claims...</td></tr>
              ) : claims.length === 0 ? (
                <tr><td colSpan={6} className="p-8 text-center text-slate-500">No claims filed yet.</td></tr>
              ) : (
                claims.map(claim => (
                  <tr key={claim.id} className="hover:bg-slate-800/50 transition">
                    <td className="p-4">
                      <span className="font-mono text-slate-300 text-sm">{claim.referenceCode}</span>
                    </td>
                    <td className="p-4 text-white font-medium">{claim.ownerName}</td>
                    <td className="p-4">
                      <span className="text-amber-400 font-mono font-bold">{claim.currency} {claim.amount?.toLocaleString()}</span>
                    </td>
                    <td className="p-4 text-sm text-slate-400">
                      {claim.submittedAt?.seconds ? new Date(claim.submittedAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="p-4">
                      <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${
                        claim.status === 'pending' ? 'bg-amber-500/10 text-amber-500 border-amber-500/20' :
                        claim.status === 'approved' ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' :
                        'bg-rose-500/10 text-rose-500 border-rose-500/20'
                      }`}>
                        {claim.status}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      <button 
                        onClick={() => openClaimModal(claim)}
                        className="bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30 text-orange-400 text-xs font-bold px-3 py-1.5 rounded-lg transition"
                      >
                        Review Claim
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Claim Review Modal */}
      {selectedClaim && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl">
            
            <div className="p-6 border-b border-slate-800 flex justify-between items-center bg-slate-950">
              <div>
                <h2 className="text-xl font-bold text-white mb-1">Claim Verification Review</h2>
                <div className="text-xs font-mono text-slate-400">Ref: {selectedClaim.referenceCode}</div>
              </div>
              <button onClick={closeClaimModal} className="text-slate-500 hover:text-white text-2xl font-bold transition">&times;</button>
            </div>

            <div className="p-6 overflow-y-auto flex-1 space-y-8">
              {/* Claim Description */}
              <div className="bg-slate-950 border border-slate-800 p-5 rounded-2xl">
                <div className="text-xs text-slate-500 font-bold uppercase tracking-wider mb-2">Claim Description</div>
                <p className="text-slate-200 text-sm leading-relaxed">{selectedClaim.description}</p>
                <div className="mt-4 pt-4 border-t border-slate-800">
                  <div className="text-xs text-slate-500 font-bold uppercase tracking-wider mb-1">Claim Amount</div>
                  <div className="text-2xl font-black text-amber-400">{selectedClaim.currency} {selectedClaim.amount?.toLocaleString()}</div>
                </div>
              </div>

              {/* Linked Asset Info */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-bold text-white mb-3">Linked Asset</h3>
                  {claimItem ? (
                    <div className="bg-slate-800 p-4 rounded-xl border border-slate-700">
                      <div className="font-bold text-white text-lg">{claimItem.make} {claimItem.model}</div>
                      <div className="text-sm text-slate-400 mb-2">ID: {claimItem.assetId}</div>
                      <div className="text-xs text-slate-300">Category: {claimItem.category}</div>
                      <div className="text-xs text-slate-300 mt-1">Condition: {claimItem.conditionScore}/100</div>
                    </div>
                  ) : <div className="text-slate-500 text-sm italic">Loading asset details...</div>}
                </div>

                {/* Linked Repair Log */}
                <div>
                  <h3 className="text-sm font-bold text-white mb-3">Target Repair Log</h3>
                  {claimRepair ? (
                    <div className="bg-slate-800 p-4 rounded-xl border border-slate-700 relative overflow-hidden">
                      <div className="absolute top-0 right-0 bg-emerald-500/20 text-emerald-400 text-[9px] font-bold px-2 py-1 uppercase tracking-widest rounded-bl-lg">Verified Authentic</div>
                      <div className="font-bold text-orange-400 mb-1">Tech: {claimRepair.technicianName}</div>
                      <div className="text-xs text-slate-400 font-mono mb-2">Cost: {claimRepair.costCurrency} {claimRepair.cost}</div>
                      <p className="text-xs text-slate-300 mb-2 truncate">{claimRepair.report}</p>
                      
                      <div className="flex gap-2">
                        {claimRepair.faultyPartPhotos?.length > 0 && (
                          <div className="text-[10px] bg-rose-500/10 text-rose-400 px-2 py-0.5 rounded border border-rose-500/20">Has Faulty Photo</div>
                        )}
                        {claimRepair.replacementPartPhotos?.length > 0 && (
                          <div className="text-[10px] bg-emerald-500/10 text-emerald-400 px-2 py-0.5 rounded border border-emerald-500/20">Has Repl Photo</div>
                        )}
                      </div>
                    </div>
                  ) : <div className="text-slate-500 text-sm italic">Loading repair data...</div>}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-slate-800 bg-slate-950 flex justify-between items-center">
              <span className="text-xs text-slate-500 font-bold uppercase tracking-wider">
                Current Status: <span className="text-white">{selectedClaim.status}</span>
              </span>
              
              <div className="flex gap-3">
                {selectedClaim.status !== 'rejected' && (
                  <button 
                    onClick={() => handleStatusUpdate(selectedClaim.id!, 'rejected')}
                    className="bg-slate-800 hover:bg-slate-700 border border-slate-600 text-rose-400 font-bold px-6 py-2 rounded-lg transition"
                  >
                    Reject Claim
                  </button>
                )}
                {selectedClaim.status !== 'approved' && (
                  <button 
                    onClick={() => handleStatusUpdate(selectedClaim.id!, 'approved')}
                    className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-6 py-2 rounded-lg shadow-lg shadow-emerald-500/20 transition"
                  >
                    Approve & Payout
                  </button>
                )}
              </div>
            </div>
            
          </div>
        </div>
      )}
    </div>
  );
};
